import SearchQuery from '@vue-storefront/core/lib/search/searchQuery'
import { remove as removeAccents } from 'remove-accents'
import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers'
import Vue from 'vue'
import config from 'config'
import { sha3_224 } from 'js-sha3'
import rootStore from "@vue-storefront/core/store";
import moment from 'moment-timezone'
import getApiEndpointUrl from '@vue-storefront/core/helpers/getApiEndpointUrl'

/**
 * Create slugify -> "create-slugify" permalink  of text
 * @param {String} text
 */
export function slugify (text) {
  // remove regional characters
  text = removeAccents(text)

  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
}

/**
 *
 * @param path
 * @param width
 * @param height
 * @param useANewS3Path
 */
export const s3Path = (path, width, height, useANewS3Path = false) => {
  let baseUrl = config.images.proxyUrl ? config.images.proxyUrl : config.images.baseUrl
  if (useANewS3Path) {
    if (width && height) {
      return `${config.api.url}/media${path}?width=${parseInt(width)}&height=${parseInt(height)}&image-type=image`
    }
    return config.api.url + `/media${path}`;
  }

  if (width && height) {
    return `${baseUrl}${path}?width=${parseInt(width)}&height=${parseInt(height)}&image-type=image`
  }
  return baseUrl + path;
}

/**
 * Head background image, shared by HomeHeadImage and the SubCategory preload hint.
 * Both must resolve to the same URL or the preload is wasted.
 */
export const headBackgroundPath = (device: string): string =>
  device === 'desktop' ? '../../../assets/bg-home.png' : '../../../assets/bg-home-mob.png'

/**
 * @param relativeUrl
 * @param width
 * @param height
 * @returns {*}
 */
export function getThumbnailPath (relativeUrl, width, height) {
  if (config.images.useExactUrlsNoProxy) {
    return relativeUrl // this is exact url mode
  } else {
    let resultUrl
    if (relativeUrl && (relativeUrl.indexOf('://') > 0 || relativeUrl.indexOf('?') > 0 || relativeUrl.indexOf('&') > 0)) relativeUrl = encodeURIComponent(relativeUrl)
    let baseUrl = config.images.proxyUrl ? config.images.proxyUrl : config.images.baseUrl // proxyUrl is not a url base path but contains {{url}} parameters and so on to use the relativeUrl as a template value and then do the image proxy opertions
    if (baseUrl.indexOf('{{') >= 0) {
      baseUrl = baseUrl.replace('{{url}}', relativeUrl)
      baseUrl = baseUrl.replace('{{width}}', width)
      baseUrl = baseUrl.replace('{{height}}', height)
      resultUrl = baseUrl
    } else {
      resultUrl = `${baseUrl}/catalog/product${relativeUrl}?width=${parseInt(width)}&height=${parseInt(height)}&image-type=image`
    }
    return relativeUrl && relativeUrl.indexOf('no_selection') < 0 ? resultUrl : config.images.productPlaceholder || ''
  }
}

/**
 * Re-format category path to be suitable for breadcrumb
 * @param {Array} categoryPath
 */
export function formatBreadCrumbRoutes (categoryPath) {
  const breadCrumbRoutesArray = []
  let partnerPrefix = ''
  let locationCode = rootStore.getters['locations/getActiveLocation']
    ? rootStore.getters['locations/getActiveLocation'].code
    : null
  let partnerCode = rootStore.getters['partners/getCurrentPartner']
    ? rootStore.getters['partners/getCurrentPartner'].code
    : null
  if (locationCode && partnerCode) {
    partnerPrefix = `/${locationCode}/${partnerCode}`
  }
  for (let category of categoryPath) {
    breadCrumbRoutesArray.push({
      name: category.name,
      route_link: partnerPrefix + formatCategoryLink(category)
    })
  }
  return breadCrumbRoutesArray
}

/**
 * Return configurable product thumbnail depending on the configurable_children
 * @param {object} product
 * @param {bool} ignoreConfig
 */
export function productThumbnailPath (product, ignoreConfig = false) {
  let thumbnail = product.image
  if ((product.type_id && product.type_id === 'configurable') && product.hasOwnProperty('configurable_children') &&
    product.configurable_children.length && (ignoreConfig || !product.is_configured) &&
    ('image' in product.configurable_children[0])
  ) {
    thumbnail = product.configurable_children[0].image
    if (!thumbnail || thumbnail === 'no_selection') {
      const childWithImg = product.configurable_children.find(f => f.image && f.image !== 'no_selection')
      if (childWithImg) {
        thumbnail = childWithImg.image
      } else {
        thumbnail = product.image
      }
    }
  }
  if (!thumbnail && product.media_gallery && product.media_gallery.length) {
    thumbnail = product.media_gallery[0].image
  }
  return thumbnail
}

export function baseFilterProductsQuery (parentCategory, filters = []) { // TODO add aggregation of color_options and size_options fields
  let searchProductQuery = new SearchQuery()
  searchProductQuery = searchProductQuery
    .applyFilter({key: 'visibility', value: {'in': [2, 3, 4]}})
    .applyFilter({key: 'status', value: {'in': [0, 1]}}) /* 2 = disabled, 4 = out of stock */

  if (config.products.listOutOfStockProducts === false) {
    searchProductQuery = searchProductQuery.applyFilter({key: 'stock.is_in_stock', value: {'eq': true}})
  }
  // Add available catalog filters
  for (let attrToFilter of filters) {
    searchProductQuery = searchProductQuery.addAvailableFilter({field: attrToFilter, scope: 'catalog'})
  }

  let childCats = [parentCategory.entity_id]
  if (parentCategory.children_data) {
    let recurCatFinderBuilder = (category) => {
      if (!category) {
        return
      }

      if (!category.children_data) {
        return
      }

      for (let sc of category.children_data) {
        if (sc && sc.entity_id) {
          childCats.push(sc.entity_id)
        }
        recurCatFinderBuilder(sc)
      }
    }
    recurCatFinderBuilder(parentCategory)
  }
  searchProductQuery = searchProductQuery.applyFilter({key: 'category_ids', value: {'in': childCats}})
  return searchProductQuery
}

export function buildFilterProductsQuery (currentCategory, chosenFilters, defaultFilters = null) {
  let filterQr = baseFilterProductsQuery(currentCategory, defaultFilters == null ? config.products.defaultFilters : defaultFilters)

  // add choosedn filters
  for (let code of Object.keys(chosenFilters)) {
    const filter = chosenFilters[code]

    if (filter.attribute_code !== 'price') {
      filterQr = filterQr.applyFilter({key: filter.attribute_code, value: {'eq': filter.id}, scope: 'catalog'})
    } else { // multi should be possible filter here?
      const rangeqr = {}
      if (filter.from) {
        rangeqr['gte'] = filter.from
      }
      if (filter.to) {
        rangeqr['lte'] = filter.to
      }
      filterQr = filterQr.applyFilter({key: filter.attribute_code, value: rangeqr, scope: 'catalog'})
    }
  }

  return filterQr
}

export function once (key, fn) {
  const { process = {} } = global
  const processKey = key + '__ONCE__'
  if (!process.hasOwnProperty(processKey)) {
    // Logger.debug(`Once ${key}`, 'helper')()
    process[processKey] = true
    fn()
  }
}

export function timezoneTime () {
  if (new Date(moment().tz('Europe/Kiev').format('YYYY-MM-DD, h:mm:ss a')).toString() !== 'Invalid Date') {
    return new Date(moment().tz('Europe/Kiev').format('YYYY-MM-DD, h:mm:ss a'))
  } else if (new Date(Date.parse(new Date().toLocaleString('en-US', {timeZone: 'Europe/Kiev'}))).toString() !== 'Invalid Date') {
    return new Date(Date.parse(new Date().toLocaleString('en-US', {timeZone: 'Europe/Kiev'})))
  }
  return new Date()
}

export const isServer: boolean = typeof window === 'undefined'

// Online/Offline helper
export const onlineHelper = Vue.observable({
  isOnline: isServer || navigator.onLine
})

export const routerHelper = Vue.observable({
  popStateDetected: false
})

if (!isServer && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

!isServer && window.addEventListener('online', () => { onlineHelper.isOnline = true })
!isServer && window.addEventListener('offline', () => { onlineHelper.isOnline = false })
!isServer && window.addEventListener('popstate', () => { routerHelper.popStateDetected = true })

export const processURLAddress = (url: string = '') => {
  if (url.startsWith('/')) return `${getApiEndpointUrl(config.api, 'url')}${url}`
  return url
}

/*
  * serial executes Promises sequentially.
  * @param {funcs} An array of funcs that return promises.
  * @example
  * const urls = ['/url1', '/url2', '/url3']
  * serial(urls.map(url => () => $.ajax(url)))
  *     .then(Logger.log.bind(Logger))()
  */
export const serial = async promises => {
  const results = []
  for (const item of promises) {
    const result = await item;
    results.push(result)
  }
  return results
}

export const isBottomVisible = () => {
  if (isServer) {
    return false
  }
  const scrollY = window.scrollY
  const visible = window.innerHeight
  const pageHeight = document.documentElement.scrollHeight
  const bottomOfPage = visible + scrollY >= pageHeight
  return bottomOfPage || pageHeight < visible
}

// helper to calcuate the hash of the shopping cart
export const calcItemsHmac = (items, token) => {
  return sha3_224(JSON.stringify({ items, token: token }))
}

function parceSchedule(str) {
  if (!str || typeof str !== 'string') {
    return [];
  }
  try {
    let json = JSON.parse(str)
    return (typeof json === 'object') ? json : []
  }
  catch (error) {
    return [];
  }
}

export function partnerWorkingTime (partner) {
  const weekday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const today = moment(new Date()).format('YYYY-MM-DD')
  let workFrom = ''
  let workTo = ''
  let isWeekend = false
  let specialWorkDay = partner.schedule.special.find(item => item.day <= today && item.day_to >= today) // search in special schedule
  const workingSchedule = (partner['schedule'] && partner.schedule['working_schedule']) ? partner.schedule['working_schedule'] : []
  if (specialWorkDay && specialWorkDay.hours && !!specialWorkDay.hours.length) {
    // set from special schedule
    workFrom = specialWorkDay.hours[0].from
    workTo = specialWorkDay.hours[specialWorkDay.hours.length - 1].to
  } else if (workingSchedule && !!workingSchedule.length) {
    // set from working schedule
    let workDay = workingSchedule.find(item => item.day === weekday[new Date().getDay()])
    if (!workDay.enable) {
      isWeekend = true // this is weekend
    }
    workFrom = workDay.from
    workTo = workDay.to
  } else {
    // set from regular schedule
    workFrom = partner.schedule.time_from
    workTo = partner.schedule.time_to
  }

  return {workFrom, workTo, isWeekend}
}

/**
 * config.partners.hiddenIds is a single VSF_HIDDEN_PARTNER_IDS env var (comma-separated
 * partner entity_ids) rather than a proper per-status rule - deliberately a manually
 * curated denylist, not derived from `enabled`/`active`/`status`, so it can hard-block a
 * specific partner (and everything under it) network-wide without depending on that
 * partner's own data being in any particular state.
 */
export function hiddenPartnerIds (): number[] {
  return (config.partners.hiddenIds || '')
    .split(',')
    .map(id => parseInt(id.trim(), 10))
    .filter(id => !isNaN(id))
}

export function isPartnerHidden (partnerId: number): boolean {
  return hiddenPartnerIds().includes(partnerId)
}
